import { useCallback, useEffect, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { DatePicker } from "rsuite";
import { add, isBefore, isFuture, parseISO } from "date-fns";
import React from "react";
import { useRootDispatch, useRootSelector } from "store/hooks";

import { BarItems, TranslationAnchor } from "components/common";
import { SliderPageWrapper } from "../../layout/sliderPageWrapper/SliderPageWrapper";
import {
  AddPaymentMethods,
  PaymentActionType,
} from "../manageWallet/addPaymentMethods/AddPaymentMethods";
import LoadTranslation from "components/common/LoadTranslation";
import { useInput, UseInput } from "hooks/useInput";
import {
  determineFeeConfiguration,
  extractError,
  isExpired,
} from "utility/utils";

import {
  calculateFee,
  checkLoanInRange,
  fetchFromConfig,
  renderConditionalProp,
  getEnumByValue,
  PAYMENT_METHOD_TYPE,
} from "utility/utils";
import { ACH, CARD, EWALLET, paymentAmountType } from "constants/common";
import {
  parsePaymentMethod,
  preparePaymentMethodAuthRequest,
} from "utility/paymentMethod";
import { getDefaultInputValue } from "utility/form";

import {
  clearSlide,
  setCurrentPaymentMethod,
  updateSlide,
} from "store/common/common-slice";
import {
  PaymentMethod,
  PaymentMethodAuthRequest,
  PaymentMethodAuthResponse,
} from "store/paymentMethods/paymentMethods-state";
import { PaymentData } from "store/payments/payments-state";
import { setPaymentData } from "store/payments/payments-slice";
import {
  activeAccountTypeConfig,
  getAccountRestrictions,
  getPaymentMethods,
  selectedAccountInfo,
} from "store/account/account-selector";
import { selectAllPaymentMethods } from "store/paymentMethods/paymentMethods-slice";

import classes from "./MakePayment.module.scss";

import { renderAccountDetailItems } from "components/pages/quickPay/expension/Expension";
import { AccountTypeConfiguration } from "store/config";
import { AccountInfo } from "store/account/account-state";
import { Disclaimer } from "components/extra/disclaimer/Disclaimer";
import {
  FT_PAYMENT_METHOD_AUTH,
  FT_PAYMENT_FEES_CONFIG,
} from "constants/request";
import { useRequest, useAddNotification } from "hooks";
import { createPaymentRequestData } from "store/payments/util";

const quickbarItems: BarItems[] = [];
export interface MakePaymentClick {
  count: number;
  scheduleFlag: boolean;
}

export const MakePayment = () => {
  const { t } = useTranslation([
    "common",
    "payment",
    "payment_next",
    "quickpay",
    "payment_final",
  ]);

  if (quickbarItems.length === 0) {
    quickbarItems.push(
      {
        title: t("payment_next:payments_label"),
        isSelected: true,
        icon: "payments",
      },
      {
        title: t("payment_next:review_label"),
        icon: "grading",
      },
      {
        title: t("payment_next:complete_label"),
        icon: "verified",
      },
    );
  }
  const dispatch = useRootDispatch();
  const addNotification = useAddNotification();

  const [buttonClick, setbuttonClick] = useState<MakePaymentClick>({
    count: 0,
    scheduleFlag: false,
  });

  const updateButtonClickFromChild = useCallback(
    (updateFn: (prev: MakePaymentClick) => MakePaymentClick) => {
      setbuttonClick((prev) => {
        const newValue = updateFn(prev);
        return newValue;
      });
    },
    [],
  );

  const { request } = useRequest();
  const selectedAccount = useRootSelector(selectedAccountInfo);
  const { restrictions } = useRootSelector(getAccountRestrictions);

  const { currentPaymentMethod } = useRootSelector((state) => state.common);
  const { user } = useRootSelector((state) => state.user);

  const config = useRootSelector(activeAccountTypeConfig);
  const paymentMethods = useRootSelector(selectAllPaymentMethods);
  const paymentMethodTypesEnabled = useRootSelector(getPaymentMethods);
  const currentPaymentConfig =
    paymentMethodTypesEnabled[currentPaymentMethod || ACH];

  const { paymentCenter, client, scheduledPayments } = useRootSelector(
    (state) => state.config,
  );

  const amountDueInRange = checkLoanInRange(
    selectedAccount?.amountDue,
    currentPaymentConfig,
  );
  const otherAmountDueInRange =
    !!selectedAccount?.otherAmountDue &&
    checkLoanInRange(selectedAccount?.otherAmountDue, currentPaymentConfig);

  const [scheduleDate, handleScheduleDate] = useState<Date>(
    add(new Date(), {
      days: 1,
    }),
  );

  const [isLoading, setIsLoading] = useState(false);
  const [selectedWalletType, setSelectedWalletType] = useState<number | null>(
    null,
  );

  const dateToBePaidType: UseInput = useInput(getDefaultInputValue("now"));

  const amountType: UseInput = useInput(
    getDefaultInputValue(
      (() => {
        if (!selectedAccount?.paymentAmountType) {
          if (amountDueInRange) {
            return paymentAmountType.due_amount;
          } else if (otherAmountDueInRange) {
            return paymentAmountType.other_due_amount;
          } else {
            return paymentAmountType.other;
          }
        } else {
          return selectedAccount.paymentAmountType;
        }
      })(),
    ),
  );

  const paymentMethodSelection: UseInput = useInput(
    getDefaultInputValue("saved_method", "saved_method"),
  );

  const paymentType: UseInput = useInput(
    getDefaultInputValue(fetchFromConfig(config, "initialPaymentTypes")),
  );

  const paymentMethod: UseInput = useInput(
    getDefaultInputValue(
      paymentMethods && paymentMethods[0] ? paymentMethods[0].id : "0",
      "0",
    ),
  );

  const otherAmount: UseInput = useInput(getDefaultInputValue("", ""));

  useEffect(() => {
    dispatch(setCurrentPaymentMethod(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethodSelection.value]);

  const getCurrentMethod = () => {
    const currentMethod = paymentMethods.find(
      (method) => method.id === parseInt(paymentMethod.value),
    );
    return currentMethod;
  };

  const convenienceFee = currentPaymentMethod
    ? determineFeeConfiguration(
        getCurrentMethod(),
        currentPaymentConfig,
        false,
        paymentCenter.defaultFees.convenienceFee!,
      )
    : null;

  useEffect(() => {
    const currentMethod = getCurrentMethod();
    if (
      currentMethod != null &&
      currentMethod.paymentMethodName !== undefined
    ) {
      dispatch(
        setCurrentPaymentMethod(
          getEnumByValue(PAYMENT_METHOD_TYPE, currentMethod.paymentMethodType!),
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethod.value]);

  useEffect(() => {
    setbuttonClick({
      count: buttonClick.count,
      scheduleFlag: dateToBePaidType.value === "now" ? false : true,
    });
  }, [dateToBePaidType.value, buttonClick.count]);

  useEffect(() => {
    const dateInputSpan = document.getElementsByClassName(
      "rs-picker-toggle-value",
    );

    if (dateInputSpan) {
      dateInputSpan[0] && dateInputSpan[0].removeAttribute("aria-placeholder");
      const textInput = document.getElementsByClassName(
        "rs-picker-toggle-textbox",
      )[0];
      if (textInput) {
        textInput.setAttribute("aria-placeholder", dateInputSpan[0].innerHTML);
      }
    }
  }, [scheduleDate]);

  const reviewpayment = () => {
    if (+amountType.value === paymentAmountType.other) {
      if (!(otherAmount.ref.current.checkValidity() && otherAmount.value > 0)) {
        otherAmount.ref.current.reportValidity();
        return;
      }
    } else {
      otherAmount.reset();
    }
    if (
      paymentMethodSelection.value === "saved_method" &&
      !paymentMethod.ref.current.checkValidity()
    ) {
      paymentMethod.ref.current.reportValidity();
      return;
    }
    if (paymentMethodSelection.value === "saved_method") {
      paymentHandler();
    } else {
      setbuttonClick((prev) => ({
        count: prev.count + 1,
        scheduleFlag: dateToBePaidType.value === "now" ? false : true,
      }));
    }
  };

  const getPaymentAmount = () => {
    let paymentAmount = selectedAccount?.amountDue; //TODO: use configration like convenience fee and escrow amount.
    if (+amountType.value === paymentAmountType.due_amount) {
      paymentAmount = selectedAccount?.amountDue;
    } else if (+amountType.value === paymentAmountType.other_due_amount) {
      paymentAmount = selectedAccount?.otherAmountDue;
    } else if (+amountType.value === paymentAmountType.other) {
      paymentAmount = parseFloat(otherAmount.value) || 0;
    }
    return paymentAmount;
  };

  const paymentHandler = async (currentMethod?: PaymentMethod) => {
    const isScheduled = dateToBePaidType.value === "now" ? false : true;

    setbuttonClick({
      count: 0,
      scheduleFlag: isScheduled,
    });

    const paymentAmount = getPaymentAmount();

    // const accountBalance = selectedAccount?.accountBalance ? selectedAccount.accountBalance : "";

    if (paymentMethodSelection.value === "saved_method") {
      const selectedMethod = paymentMethods?.filter((single: any) => {
        return +single.id === +paymentMethod.value;
      });
      if (selectedMethod) currentMethod = selectedMethod[0];
    } else {
      if (
        currentMethod &&
        !currentMethod.ack &&
        currentMethod.paymentMethodType !== PAYMENT_METHOD_TYPE.eWallet
      ) {
        // Now we are allowing all payment method types to use this. At the time this is ACH and Card,
        // we will need to consider when ewallet is done.
        const authData: PaymentMethodAuthRequest =
          preparePaymentMethodAuthRequest(currentMethod);

        authData.accountType = selectedAccount?.accountType;

        try {
          setIsLoading(true);

          const paymentMethodAuth =
            await request.post<PaymentMethodAuthResponse>(
              FT_PAYMENT_METHOD_AUTH,
              authData,
            );

          if (paymentMethodAuth.data.authReferenceId) {
            currentMethod.authReferenceId =
              paymentMethodAuth.data.authReferenceId;
          }
          if (paymentMethodAuth.data.cardBinType) {
            currentMethod.cardBinType = paymentMethodAuth.data.cardBinType;
          }
        } catch (e) {
          const errCode = extractError(e);
          addNotification({
            messages: errCode,
            type: "ERROR",
            closable: true,
          });

          setIsLoading(false);
          // Return early to cancel the flow
          return;
        }
      }
    }

    let dataToSend: PaymentData = {
      paymentDate:
        dateToBePaidType.value === "now"
          ? new Date().toUTCString()
          : scheduleDate.toUTCString(),
      paymentAmount: paymentAmount,
      paymentAmountType:
        scheduledPayments?.lockScheduledDueAmount &&
        isScheduled &&
        +amountType.value !== paymentAmountType.other
          ? paymentAmountType.locked_due_amount
          : parseInt(amountType.value),
      feeTotal: calculateFee(
        convenienceFee,
        paymentCenter.defaultFees.convenienceFee,
        paymentAmount,
      ),
      paymentMethod: currentMethod ? currentMethod : {},
      accountInformationId: selectedAccount?.id,
      paymentType: paymentType.value,
      savedPaymentMethodId: currentMethod?.id ? currentMethod?.id! : 0,
      firstName: user?.givenName!,
      lastName: user?.familyName!,
      email: user?.email!,
      applicationName: "payment-center",
      walletType: currentMethod?.walletType || null,
      jsonPayload: currentMethod?.jsonPayload || undefined,

      paymentMethodSelectionType: currentPaymentMethod || "",
      clientId: client.clientId || 0,
    };

    request(`${FT_PAYMENT_FEES_CONFIG}/calculate`, {
      data: createPaymentRequestData(dataToSend, false),
      method: "POST",
    })
      .then((response: any) => {
        if (response && response.data) {
          dataToSend = { ...dataToSend, feeTotal: response.data.feeTotal };
        }
        dispatch(setPaymentData(dataToSend));
        setIsLoading(false);
        dispatch(updateSlide({ id: "MakeAPaymentReview" }));
      })
      .catch((err) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const cancelButtonHandler = () => {
    dispatch(clearSlide());
  };

  const renderPaymentMethods = (paymentMethods: PaymentMethod[]) => {
    return paymentMethods
      .filter((single) => !isExpired(single.expirationDate))
      .map((single, i) => {
        const { title } = parsePaymentMethod(single);
        const methodType = getEnumByValue(
          PAYMENT_METHOD_TYPE,
          single.paymentMethodType!,
        );
        let paymentTypeRestricted = false;
        switch (methodType) {
          case ACH:
            paymentTypeRestricted = restrictions.achRestricted;
            break;
          case CARD:
            paymentTypeRestricted = restrictions.cardRestricted;
            break;
          default:
            break;
        }

        if (
          methodType !== null &&
          paymentMethodTypesEnabled[methodType] &&
          !paymentTypeRestricted
        ) {
          return (
            <ft-list-item
              key={i}
              {...renderConditionalProp(
                paymentMethod.value === single.id,
                "selected",
              )}
              value={single.id}
            >
              {title}
            </ft-list-item>
          );
        }
        return null;
      });
  };

  const datePickerOpenHandler = () => {
    const datePicker = document.getElementsByClassName("rs-picker-menu");
    datePicker[0] && datePicker[0].removeAttribute("inert");
  };

  const renderPaymentType = () => {
    const items: JSX.Element[] = [];
    config.paymentTypes.forEach((paymentTypeConfig: any) => {
      const item = (
        <ft-list-item
          key={paymentTypeConfig.code}
          {...renderConditionalProp(
            paymentType.value === paymentTypeConfig.code,
            "selected",
          )}
          value={paymentTypeConfig.code}
        >
          {paymentTypeConfig.name}
        </ft-list-item>
      );
      items.push(item);
    });
    return items;
  };

  const consumeRenderedItems = (
    accountInfo: AccountInfo,
    t: any,
    config: AccountTypeConfiguration,
  ) => {
    const renderedItems = renderAccountDetailItems(accountInfo, t, config);
    const formattedData: JSX.Element[] = [];
    if (renderedItems && renderedItems.props.children) {
      const childrenArray = React.Children.toArray(
        renderedItems.props.children,
      );
      const accountNumberIndex = childrenArray.findIndex((child: any) => {
        const displayName = child.props.children[0]?.props.children;
        return displayName === t("payment:accountNumber");
      });
      if (accountNumberIndex !== -1) {
        const accountNumberItem = childrenArray.splice(
          accountNumberIndex,
          1,
        )[0];
        childrenArray.unshift(accountNumberItem);
      }
      childrenArray.forEach((child: React.ReactNode, index: number) => {
        if ((child as JSX.Element).props?.children?.length === 2) {
          const displayName = (child as JSX.Element).props.children[0]?.props
            .children;
          if (displayName != null) {
            const value = (child as JSX.Element).props.children[1]?.props
              .children;
            let overDueClass = "";
            if (displayName === t("common:due_date")) {
              if (!isFuture(parseISO(value))) overDueClass = classes.Error;
            }
            const formattedValue = (
              <div key={index} className={classes.ContainerItem}>
                <p className={overDueClass}>{displayName} </p>
                <p className={overDueClass}>{value}</p>
              </div>
            );
            formattedData.push(formattedValue);
          }
        }
      });
    }
    return <div>{formattedData}</div>;
  };

  return (
    <SliderPageWrapper
      quickbarItems={{ items: quickbarItems, progress: "0.2" }}
      headerTitle={t("common:make_payment_label")}
    >
      {selectedAccount ? (
        <div className={classes.Wrapper}>
          <div>{consumeRenderedItems(selectedAccount, t, config)}</div>

          <ft-divider class={classes.Divider}></ft-divider>
          <div className={classes.SectionWrapper}>
            <p className={classes.Heading}>
              {t("payment_final:payment_amount")}
            </p>
            {currentPaymentConfig?.payments?.amountSelectionOption.includes(
              "amount-due",
            ) &&
            (amountDueInRange || otherAmountDueInRange) ? (
              <>
                {amountDueInRange ? (
                  <ft-formfield
                    label={`${t("common:amount_due")}: ${t(
                      "common:currency_format",
                      {
                        value: selectedAccount?.amountDue,
                      },
                    )}`}
                  >
                    <ft-radio
                      {...renderConditionalProp(
                        +amountType.value === paymentAmountType.due_amount,
                        "checked",
                      )}
                      onClick={amountType.valueChangeHandler}
                      name="amount-type"
                      value={paymentAmountType.due_amount}
                      data-testid="make-payment-page-amount-due"
                    ></ft-radio>
                  </ft-formfield>
                ) : null}
                {otherAmountDueInRange && (
                  <ft-formfield
                    label={`${t("common:other_amount_due")}: ${t(
                      "common:currency_format",
                      { value: selectedAccount?.otherAmountDue },
                    )}`}
                  >
                    <ft-radio
                      {...renderConditionalProp(
                        +amountType.value ===
                          paymentAmountType.other_due_amount,
                        "checked",
                      )}
                      onClick={amountType.valueChangeHandler}
                      name="payment-amount"
                      value={paymentAmountType.other_due_amount}
                    ></ft-radio>
                  </ft-formfield>
                )}
              </>
            ) : null}
            {currentPaymentConfig?.payments?.amountSelectionOption.includes(
              "user-provided",
            ) && (
              <>
                <ft-formfield label={`${t("payment_next:other_amount")} $:`}>
                  <ft-radio
                    {...renderConditionalProp(
                      +amountType.value === paymentAmountType.other,
                      "checked",
                    )}
                    onClick={amountType.valueChangeHandler}
                    name="amount-type"
                    value={paymentAmountType.other}
                    data-testid="make-payment-page-other-amount"
                  ></ft-radio>
                </ft-formfield>
                <ft-textfield
                  class={classes.PaymentAmount_amount}
                  outlined
                  label={t("payment:amount")}
                  customInput="test"
                  type={"number"}
                  step={".01"}
                  min={
                    currentPaymentConfig.payments.minimumPaymentAmount || 0.1
                  }
                  max={currentPaymentConfig.payments.maximumPaymentAmount}
                  ref={otherAmount.ref}
                  validationMessage={t("payment_next:greater_than_zero", {
                    min: currentPaymentConfig.payments.minimumPaymentAmount,
                    max: currentPaymentConfig.payments.maximumPaymentAmount,
                  })}
                  onInput={otherAmount.decimalValueHandler}
                  value={otherAmount.value}
                  onBlur={otherAmount.inputBlurHandler}
                  {...renderConditionalProp(
                    +amountType.value === paymentAmountType.other,
                    "required",
                  )}
                  icon="attach_money"
                  {...renderConditionalProp(
                    +amountType.value !== paymentAmountType.other,
                    "disabled",
                  )}
                  data-testid="make-payment-page-amount-due-textfield"
                  onWheel={(e: WheelEvent) =>
                    e.target instanceof HTMLElement && e.target.blur()
                  }
                ></ft-textfield>
              </>
            )}
          </div>
          {Object.keys(config.paymentTypes).length === 1 ? null : (
            <>
              <ft-divider class={classes.Divider}></ft-divider>
              <div className={classes.SectionWrapper}>
                <p className={classes.Heading}>
                  {t("payment_next:payment_type")}
                </p>
                <ft-select
                  validationMessage=" "
                  onClick={paymentType.valueChangeHandler}
                  value={paymentType.value}
                  onBlur={paymentType.inputBlurHandler}
                  outlined
                  label={t("payment_next:payment_type")}
                  id="payment-type"
                  class={classes.PaymentTypeSelect}
                  data-testid="make-payment-page-payment-type"
                >
                  {renderPaymentType()}
                </ft-select>
              </div>
            </>
          )}
          <ft-divider class={classes.Divider}></ft-divider>
          <div className={classes.SectionWrapper}>
            <p className={classes.Heading}>
              {t("payment_next:date_to_be_paid")}
            </p>
            <ft-formfield
              label={`${t("common:today")}: ${t("common:date_format", {
                value: new Date(),
              })}`}
            >
              <ft-radio
                {...renderConditionalProp(
                  dateToBePaidType.value === "now",
                  "checked",
                )}
                onClick={dateToBePaidType.valueChangeHandler}
                name="date-to-be-paid-type"
                value="now"
                data-testid="make-payment-page-payment-now"
              ></ft-radio>
            </ft-formfield>
            {currentPaymentConfig?.scheduledPayments?.enabled && (
              <>
                <ft-formfield label={t("payment_next:schedule_for_future")}>
                  <ft-radio
                    {...renderConditionalProp(
                      dateToBePaidType.value === "scheduled",
                      "checked",
                    )}
                    onClick={dateToBePaidType.valueChangeHandler}
                    name="date-to-be-paid-type"
                    value="scheduled"
                    data-testid="make-payment-page-payment-scheduled"
                  ></ft-radio>
                </ft-formfield>
                <div className="rsuite-custom">
                  <DatePicker
                    cleanable={false}
                    shouldDisableDate={(date: any) =>
                      isBefore(date, new Date())
                    }
                    isoWeek
                    onOpen={datePickerOpenHandler}
                    block
                    oneTap
                    format="MM-dd-yyyy"
                    placeholder={t("payment:date")}
                    value={scheduleDate}
                    onChange={(date: Date) => handleScheduleDate(date)}
                    {...renderConditionalProp(
                      dateToBePaidType.value !== "scheduled",
                      "disabled",
                    )}
                    size="lg"
                    placement="auto"
                    data-testid="make-payment-page-payment-scheduled-date"
                  />
                </div>
              </>
            )}
          </div>
          <ft-divider class={classes.Divider}></ft-divider>
          <div className={classes.SectionWrapper}>
            <p className={classes.Heading}>
              {t("payment_next:choose_payment_method")}
            </p>
            <span className={classes.ConvenienceFee}>
              {t("payment_next:processing_fee")}
            </span>
            {paymentMethods && paymentMethods[0] ? (
              <>
                <ft-formfield label={`${t("common:saved_payment_methods")}`}>
                  <ft-radio
                    {...renderConditionalProp(
                      paymentMethodSelection.value === "saved_method",
                      "checked",
                    )}
                    onClick={paymentMethodSelection.valueChangeHandler}
                    name="payment-method-selection"
                    value="saved_method"
                    data-testid="make-payment-page-saved-payment-method"
                  ></ft-radio>
                </ft-formfield>
                {paymentMethodSelection.value === "saved_method" && (
                  <ft-select
                    ref={paymentMethod.ref}
                    validationMessage={t("common:field_not_empty")}
                    required
                    onClick={paymentMethod.valueChangeHandler}
                    value={paymentMethod.value}
                    onBlur={paymentMethod.inputBlurHandler}
                    outlined
                    label={t("payment_next:choose_payment_method")}
                    id="payment-method"
                    class={classes.SavedPaymentMethod}
                    data-testid="make-payment-page-select-saved-payment-method"
                  >
                    {paymentMethods
                      ? renderPaymentMethods(paymentMethods)
                      : null}
                  </ft-select>
                )}
              </>
            ) : null}
            <ft-formfield label={`${t("payment_next:new_payment_method")}`}>
              <ft-radio
                {...renderConditionalProp(
                  paymentMethodSelection.value === "new_method",
                  "checked",
                )}
                onClick={paymentMethodSelection.valueChangeHandler}
                name="payment-method-selection"
                value="new_method"
                data-testid="make-payment-page-new-payment-method"
              ></ft-radio>
            </ft-formfield>
            {paymentMethodSelection.value === "new_method" && (
              <AddPaymentMethods
                buttonClick={buttonClick}
                updateButtonClick={updateButtonClickFromChild}
                paymentHandler={paymentHandler}
                paymentAmount={getPaymentAmount()}
                inlineView={true}
                borderView={true}
                paymentActionType={PaymentActionType.SAVE_AND_PAY_LATER}
                selectedWalletType={selectedWalletType}
                setSelectedWalletType={setSelectedWalletType}
              />
            )}
          </div>
          <ft-divider class={classes.Divider}></ft-divider>
          <Disclaimer
            translatedText={
              <Trans
                i18nKey="quickpay:disclaimer_text"
                values={{
                  convenienceFee: paymentCenter.defaultFees.convenienceFee,
                }}
                components={{ a: <TranslationAnchor /> }}
              />
            }
          />
          <div className={classes.Buttons}>
            <ft-button
              class={[classes.Back, "outline-button"].join(" ")}
              label={t("common:cancel_label")}
              outlined
              onClick={cancelButtonHandler}
              data-testid="make-payment-page-cancel-button"
            ></ft-button>
            <ft-button
              class={classes.Make_payment}
              label={t("payment_next:review_payment")}
              unelevated
              {...renderConditionalProp(
                (paymentMethod.value === "0" &&
                  paymentMethodSelection.value === "saved_method") ||
                  selectedAccount.active === false,
                "disabled",
              )}
              data-testid="make-payment-page-review-button"
              onClick={reviewpayment}
            ></ft-button>
          </div>
        </div>
      ) : null}
      <LoadTranslation keys={["payment_final"]} />
      {isLoading ? <ft-screen-loader></ft-screen-loader> : null}
    </SliderPageWrapper>
  );
};
